import React, { useState, useEffect, useMemo, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import Axios from "../../../services/AxiosInstance";
import { getGreeting, formatLastLogin } from "../../common/helpers";
import DashboardCard from "../../common/DashboardCard";
import { useCountChangeDetector } from "../../common/useCountChangeDetector";

// ponytail: Stat card icon and color config
const STAT_CONFIG = {
  courses: { icon: "fe-book", color: "#4f46e5", bg: "#eef2ff" },
  placements: { icon: "fe-briefcase", color: "#059669", bg: "#ecfdf5" },
  partners: { icon: "fe-users", color: "#d97706", bg: "#fffbeb" },
  issues: { icon: "fe-alert-circle", color: "#dc2626", bg: "#fef2f2" },
  users: { icon: "fe-user", color: "#0891b2", bg: "#ecfeff" },
};

// ponytail: Feature card config
const FEATURE_CARDS = [
  {
    label: "Courses",
    icon: "fe-book",
    path: "/course-list",
    desc: "Manage and publish training courses",
    requiredPermissions: ["course_list", "add_course"],
  },
  {
    label: "Placements",
    icon: "fe-briefcase",
    path: "/job-list",
    desc: "Post jobs and track applications",
    requiredPermissions: ["job_list", "create_job", "job_application"],
  },
  {
    label: "Partners",
    icon: "fe-users",
    path: "/partner-list",
    desc: "Manage partner collaborations",
    requiredPermissions: ["partner_list", "admin-projects", "interview_schedule", "request_training"],
  },
  {
    label: "Access Control",
    icon: "fe-shield",
    path: "/role-management",
    desc: "Manage roles and permissions",
    requiredPermissions: [],
  },
  {
    label: "Issue Listings",
    icon: "fe-alert-circle",
    path: "/IssueList",
    desc: "Track and resolve support tickets",
    requiredPermissions: [],
  },
  {
    label: "HR",
    icon: "fe-activity",
    path: "/hr-actions",
    desc: "View HR audit trail across the portal",
    requiredPermissions: [],
  },
  {
    label: "Users",
    icon: "fe-user",
    path: "/user-role-management",
    desc: "Manage system users",
    requiredPermissions: [],
  },
];

const FEATURE_TO_STAT = {
  Courses: "courses",
  Placements: "placements",
  Partners: "partners",
  "Issue Listings": "issues",
  Users: "users",
};

const STAT_DISPLAY_LABEL = {
  courses: "Courses",
  placements: "Placements",
  partners: "Partners",
  issues: "Open Issues",
  users: "Registered Users",
};

// ponytail: module config for activity icons, colors, and navigation
const MODULE_CONFIG = {
  course: { icon: "fe-book", color: "#4f46e5", path: "/course-list", permission: "course_list" },
  job: { icon: "fe-briefcase", color: "#059669", path: "/job-list", permission: "job_list" },
  issue: { icon: "fe-alert-circle", color: "#dc2626", path: "/IssueList", permission: null },
};

// ponytail: relative time formatter
const getRelativeTime = (date) => {
  const diff = Math.floor((Date.now() - date) / 1000);
  if (diff < 60) return "Just now";
  if (diff < 3600) return `${Math.floor(diff / 60)} min ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)} hours ago`;
  if (diff < 172800) return "Yesterday";
  return `${Math.floor(diff / 86400)} days ago`;
};

// ponytail: attempts to extract a valid Date from any common timestamp format
const extractDate = (item) => {
  if (!item) return null;
  // Firestore Timestamp with _seconds
  if (item.createdAt?._seconds != null) return new Date(item.createdAt._seconds * 1000);
  // Firestore Timestamp with toDate method
  if (item.createdAt?.toDate) return item.createdAt.toDate();
  // string or number date fields
  if (item.added_date) {
    const d = new Date(item.added_date);
    if (!isNaN(d.getTime())) return d;
  }
  if (item.createdAt) {
    // avoid passing Firestore Timestamp object to Date constructor
    if (typeof item.createdAt === "string" || typeof item.createdAt === "number") {
      const d = new Date(item.createdAt);
      if (!isNaN(d.getTime())) return d;
    }
  }
  // Firestore _seconds directly on the item (date_time_stamp, dateTimeStamp)
  if (item._seconds != null) return new Date(item._seconds * 1000);
  if (item.date_time_stamp) {
    const d = new Date(item.date_time_stamp);
    if (!isNaN(d.getTime())) return d;
  }
  return null;
};

// ponytail: fetch activities from centralized API + per-module fallbacks
const useRecentActivities = (permissions) => {
  const [activities, setActivities] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      const results = [];

      const canAccess = (perm) => permissions.length === 0 || !perm || permissions.includes(perm);

      const fetches = [];

      // ponytail: centralized activity API - logged actions (Issue resolved, Issue updated)
      fetches.push(
        Axios.get("/api/activities?limit=10").then(res => {
          const items = res.data?.data || [];
          items.forEach(a => {
            const ts = extractDate(a);
            if (ts) {
              const cfg = a.module?.toLowerCase() === "issues" ? MODULE_CONFIG.issue
                : a.module?.toLowerCase() === "courses" ? MODULE_CONFIG.course
                : a.module?.toLowerCase() === "placements" ? MODULE_CONFIG.job
                : { icon: "fe-clock", color: "#6b7280", path: null, permission: null };
              results.push({
                type: a.module?.toLowerCase() || "general",
                id: a.entityId || a.id,
                label: a.label || a.action,
                action: a.action,
                module: a.module || "General",
                timestamp: ts,
                ...cfg,
              });
            }
          });
        }).catch(() => {})
      );

      // ponytail: fallback - reused existing API for courses
      if (canAccess("course_list")) {
        fetches.push(
          Axios.get("/api/courses").then(res => {
            const items = Array.isArray(res.data) ? res.data : [];
            items.forEach(c => {
              const ts = extractDate(c);
              if (ts) results.push({
                type: "course", id: c.id, label: c.courseName || "Untitled",
                action: "New course created", module: "Courses",
                timestamp: ts, ...MODULE_CONFIG.course,
              });
            });
          }).catch(() => {})
        );
      }

      // ponytail: fallback - reused existing API for jobs
      if (canAccess("job_list")) {
        fetches.push(
          Axios.get("/api/jobs").then(res => {
            const items = res.data?.data || [];
            items.forEach(j => {
              const ts = extractDate(j);
              if (ts) results.push({
                type: "job", id: j.id, label: j.companyName || "Untitled",
                action: "New job posted", module: "Placements",
                timestamp: ts, ...MODULE_CONFIG.job,
              });
            });
          }).catch(() => {})
        );
      }

      // ponytail: reused existing API for issues - captures partner-created "New issue raised"
      if (canAccess(null)) {
        fetches.push(
          Axios.get("/api/issues?pageNo=1&perPage=10").then(res => {
            const items = res.data?.data?.data || [];
            items.forEach(i => {
              const ts = extractDate(i);
              if (ts) results.push({
                type: "issue", id: i.issue_id,
                label: i.business_name || i.problem_title || "Issue",
                action: i.status?.toLowerCase() === "resolved" ? "Issue resolved" : "New issue raised",
                module: "Issues",
                timestamp: ts, ...MODULE_CONFIG.issue,
              });
            });
          }).catch(() => {})
        );
      }

      await Promise.allSettled(fetches);
      // ponytail: remove exact duplicates by type+id+action
      const seen = new Set();
      const deduped = results.filter(r => {
        const key = `${r.type}-${r.id}-${r.action}`;
        if (seen.has(key)) return false;
        seen.add(key);
        return true;
      });
      deduped.sort((a, b) => b.timestamp - a.timestamp);
      if (!cancelled) {
        setActivities(deduped.slice(0, 5));
        setLoading(false);
      }
    };
    load();
    return () => { cancelled = true; };
  }, [permissions]);

  return { activities, loading };
};

const playNotificationSound = (() => {
  let audioCtx = null;
  return () => {
    try {
      if (!audioCtx) audioCtx = new (window.AudioContext || window.webkitAudioContext)();
      const osc = audioCtx.createOscillator();
      const gain = audioCtx.createGain();
      osc.connect(gain);
      gain.connect(audioCtx.destination);
      osc.frequency.setValueAtTime(880, audioCtx.currentTime);
      osc.frequency.exponentialRampToValueAtTime(660, audioCtx.currentTime + 0.15);
      gain.gain.setValueAtTime(0.15, audioCtx.currentTime);
      gain.gain.exponentialRampToValueAtTime(0.001, audioCtx.currentTime + 0.3);
      osc.start(audioCtx.currentTime);
      osc.stop(audioCtx.currentTime + 0.3);
    } catch (e) {}
  };
})();

const QuickAccessCard = ({ card, statValue, notification }) => {
  const navigate = useNavigate();
  const [showNotif, setShowNotif] = useState(false);
  const [highlight, setHighlight] = useState(false);
  const [displayValue, setDisplayValue] = useState(statValue);
  const [notifMessage, setNotifMessage] = useState("");
  const changeKeyRef = useRef(null);

  const animateValue = useCallback((from, to) => {
    const steps = 20;
    const diff = to - from;
    const stepSize = diff / steps;
    const duration = 500;
    const intervalMs = duration / steps;
    let step = 0;

    setDisplayValue(from);
    const anim = setInterval(() => {
      step++;
      if (step >= steps) {
        setDisplayValue(to);
        clearInterval(anim);
      } else {
        setDisplayValue(Math.round(from + stepSize * step));
      }
    }, intervalMs);
    return () => clearInterval(anim);
  }, []);

  useEffect(() => {
    if (notification && notification.changeKey !== changeKeyRef.current) {
      changeKeyRef.current = notification.changeKey;
      setNotifMessage(notification.message);
      setShowNotif(true);
      setHighlight(true);
      playNotificationSound();

      const cleanupAnim = animateValue(notification.previous, notification.current);

      const dismissTimer = setTimeout(() => setShowNotif(false), 7000);
      const highlightTimer = setTimeout(() => setHighlight(false), 5000);

      return () => {
        cleanupAnim();
        clearTimeout(dismissTimer);
        clearTimeout(highlightTimer);
      };
    } else if (!notification) {
      setDisplayValue(statValue);
    }
  }, [notification, statValue, animateValue]);

  useEffect(() => {
    if (!notification) setDisplayValue(statValue);
  }, [statValue, notification]);

  const statKey = FEATURE_TO_STAT[card.label];
  const displayLabel = statKey ? STAT_DISPLAY_LABEL[statKey] : null;

  return (
    <div
      className={`dashboard-card ${highlight ? 'stat-card--highlighted' : ''}`}
      onClick={() => navigate(card.path)}
    >
      <div className="dashboard-card-body">
        <div className="card-icon-wrapper">
          <i className={`${card.icon} card-icon`}></i>
        </div>
        <h5 className="card-label">{card.label}</h5>
        {displayLabel !== null && (
          <div className={`card-stat-value ${highlight ? 'stat-value--bounce' : ''}`}>
            {displayValue} {displayLabel}
          </div>
        )}
        <p className="card-desc">{card.desc}</p>
        <div className={`stat-notification ${showNotif ? 'stat-notification--visible' : ''}`}>
          {notifMessage}
        </div>
        {highlight && <span className="stat-badge">UPDATED</span>}
      </div>
    </div>
  );
};

const NewDashboard = () => {
  const navigate = useNavigate();
  const [permissions, setPermissions] = useState([]);
  const [stats, setStats] = useState({
    courses: { value: 0, change: "+12%" },
    placements: { value: 0, change: "+8%" },
    partners: { value: 0, change: "+5%" },
    issues: { value: 0, change: "-3%" },
    users: { value: 0, change: "+18%" },
  });
  const intervalRef = useRef(null);

  // ponytail: Load permissions
  useEffect(() => {
    const loadPermission = async () => {
      try {
        const roleId = localStorage.getItem("role_id");

        // Multi-role support: prefer stored role_ids list,
        // fall back to single role_id
        let roleParam = roleId;

        const storedRoleIds =
          localStorage.getItem("role_ids");

        if (storedRoleIds) {

          try {

            const parsed =
              JSON.parse(storedRoleIds);

            if (
              Array.isArray(parsed) &&
              parsed.length > 0
            ) {

              roleParam = parsed.join(",");

            }

          } catch (err) {

            console.log(err);

          }

        }

        const API_URL = process.env.REACT_APP_API_URL;
        const res = await axios.get(`${API_URL}api/admin/role-permissions/${roleParam}`);
        setPermissions(res.data.permissions || []);
      } catch (err) {
        console.log(err);
      }
    };
    loadPermission();
  }, []);

  const fetchStats = useCallback(async () => {
    try {
      const [coursesRes, jobsRes, partnersRes, issuesRes, usersRes] = await Promise.allSettled([
        Axios.get("/api/courses"),
        Axios.get("/api/jobs"),
        Axios.get("/api/courses/partners"),
        Axios.get("/api/issues?pageNo=1&perPage=1"),
        Axios.get("/api/admin/list-user"),
      ]);
      setStats(prev => ({
        courses: { value: coursesRes.value?.data?.length ?? prev.courses.value, change: "+12%" },
        placements: { value: jobsRes.value?.data?.data?.length ?? prev.placements.value, change: "+8%" },
        partners: { value: partnersRes.value?.data?.length ?? prev.partners.value, change: "+5%" },
        issues: { value: issuesRes.value?.data?.data?.totalPage ?? prev.issues.value, change: "-3%" },
        users: { value: usersRes.value?.data?.data?.UerList?.length ?? prev.users.value, change: "+18%" },
      }));
    } catch (err) {
      console.log("Stats fetch error (using placeholders):", err);
    }
  }, []);

  useEffect(() => {
    fetchStats();
    intervalRef.current = setInterval(fetchStats, 30000);
    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, [fetchStats]);

  const counts = useMemo(() => ({
    courses: stats.courses.value,
    placements: stats.placements.value,
    partners: stats.partners.value,
    issues: stats.issues.value,
    users: stats.users.value,
  }), [stats]);

  const changes = useCountChangeDetector(counts);

  const greeting = useMemo(() => getGreeting(), []);

  // ponytail: user name from localStorage (set during login)
  const userName = localStorage.getItem("user_name") || "Admin";

  const lastLogin = useMemo(() => {
    const stored = localStorage.getItem("last_login");
    return formatLastLogin(stored);
  }, []);

  // ponytail: Filter feature cards by RBAC
  const visibleCards = FEATURE_CARDS.filter(
    (c) =>
      c.requiredPermissions.length === 0 ||
      c.requiredPermissions.some((p) => permissions.includes(p))
  );

  // ponytail: dynamic recent activities from existing APIs
  const { activities, loading } = useRecentActivities(permissions);

  return (
    <div className="dashboard-wrapper">
      {/* ponytail: Welcome Section */}
      <div className="welcome-section">
        <div className="welcome-text">
          <h1 className="welcome-greeting">{greeting.text}, {userName} {greeting.emoji}</h1>
          <p className="welcome-subtext">Welcome back to VedMG Technologies.</p>
          {lastLogin && <span className="welcome-date">Last Login: {lastLogin}</span>}
        </div>
      </div>

      {/* ponytail: Quick Access Cards with Live Stats */}
      <div className="section-header">
        <h5 className="section-title">Quick Access</h5>
      </div>
      <div className="row g-3 mb-4">
        {visibleCards.map((card, index) => {
          const statKey = FEATURE_TO_STAT[card.label];
          const statValue = statKey ? stats[statKey]?.value ?? 0 : null;
          const change = statKey ? changes[statKey] || null : null;
          return (
            <div key={index} className="col-md-6 col-xl-4">
              <QuickAccessCard
                card={card}
                statValue={statValue}
                notification={change}
              />
            </div>
          );
        })}
      </div>

      {/* ponytail: Recent Activity - dynamically sourced from existing APIs */}
      <div className="row g-3 mb-4">
        <div className="col-lg-6">
          <div className="dashboard-chart-card">
            <h5 className="chart-card-title">Recent Activity</h5>
            {loading ? (
              <div className="recent-activity-empty">Loading...</div>
            ) : activities.length === 0 ? (
              <div className="recent-activity-empty">No recent activity available.</div>
            ) : (
              <div className="recent-activity-list">
                {activities.map((item, i) => (
                  <div
                    key={`${item.type}-${item.id || i}`}
                    className="recent-activity-item"
                    onClick={() => navigate(item.path)}
                    style={{ cursor: item.path ? "pointer" : "default" }}
                  >
                    <div className="activity-icon" style={{ backgroundColor: item.color }}>
                      <i className={item.icon}></i>
                    </div>
                    <div className="activity-content">
                      <p className="activity-text">{item.action}</p>
                      <span className="activity-time">{getRelativeTime(item.timestamp)}</span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default NewDashboard;
